import { Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useDeleteUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import AddGrowerSection from './add-grower-section';
import NewGrowersModalSidebar from './sidebar';
import { SingleGrowerFormProvider, useSingleGrowerFormApi } from './single-grower-form-context';

export type SidebarGrower = {
    id?: number;
    name?: string;
    cuit: string;
    segment?: string;
    advisor?: string;
    rv?: string;
    location?: string;
    email?: string;
    phone?: string;
    editable: boolean;
};

function NewGrowersModalContent({
    open,
    setOpen,
    onAddFields,
    singleGrower,
    onGrowerAdded,
    defaultRv,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onAddFields: (growerUserNodeId?: number) => void;
    singleGrower: boolean;
    onGrowerAdded?: (growerUserNodeId?: number) => void;
    defaultRv?: string;
}) {
    const [growers, setGrowers] = useState<SidebarGrower[]>([]);
    const [editingGrowerIndex, setEditingGrowerIndex] = useState<number | null>(null);
    const deleteUserNodeMutation = useDeleteUserNode();
    const singleGrowerFormApi = useSingleGrowerFormApi();

    useEffect(() => {
        if (open) {
            setGrowers([]);
            setEditingGrowerIndex(null);
        }
    }, [open]);

    const handleAddGrower = (grower: SidebarGrower) => {
        setGrowers((prev) => [...prev, grower]);
        setEditingGrowerIndex(null);
    };

    const handleUpdateGrower = (index: number, grower: SidebarGrower) => {
        setGrowers((prev) => {
            const next = [...prev];
            next[index] = grower;
            return next;
        });
        setEditingGrowerIndex(null);
    };

    const handleDeleteGrower = async (index: number) => {
        const grower = growers[index];
        if (grower?.id !== undefined) {
            await deleteUserNodeMutation.trigger(grower.id);
        }
        setGrowers((prev) => prev.filter((_, i) => i !== index));
        setEditingGrowerIndex(null);
    };

    const handleSaveAndAddFields = async () => {
        if (!singleGrowerFormApi) return;
        const { success, growerId } = await singleGrowerFormApi.submit();
        if (success) {
            setOpen(false);
            onAddFields(growerId);
            onGrowerAdded?.(growerId);
        }
    };

    return (
        <ModalV2
            open={open}
            onClose={() => setOpen(false)}
            title={singleGrower ? _('Agregar productor') : _('Agregar productores')}
            maxWidth='md'
            bodyProps={{
                alignItems: 'stretch',
                textAlign: 'left',
                width: '100%',
                spacing: 0,
            }}
            actions={
                !singleGrower ? (
                    <>
                        <Button variant='text' color='secondary' onClick={() => setOpen(false)}>
                            {_('Finalizar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => onAddFields()}
                            disabled={growers.length === 0}
                        >
                            {_('Agregar los lotes por productor')}
                        </Button>
                    </>
                ) : (
                    <Button
                        type='button'
                        variant='contained'
                        color='secondary'
                        onClick={handleSaveAndAddFields}
                        disabled={!singleGrowerFormApi?.isValid || singleGrowerFormApi?.isSubmitting}
                        loading={singleGrowerFormApi?.isSubmitting}
                    >
                        {_('Guardar y agregar lotes')}
                    </Button>
                )
            }
            actionsProps={{ sx: { justifyContent: 'flex-end' } }}
        >
            <Grid2 container spacing={2} width='100%'>
                <Grid2 xs={12} md={singleGrower ? 12 : 8} sx={{ overflow: 'auto' }}>
                    <AddGrowerSection
                        singleGrower={singleGrower}
                        growers={growers}
                        editingGrowerIndex={editingGrowerIndex}
                        onAddGrower={handleAddGrower}
                        onUpdateGrower={handleUpdateGrower}
                        defaultRv={defaultRv}
                    />
                </Grid2>
                {!singleGrower && (
                    <Grid2 xs={12} md sx={{ overflow: 'auto' }}>
                        <NewGrowersModalSidebar
                            growers={growers}
                            onEdit={(index) => setEditingGrowerIndex(index)}
                            onDelete={handleDeleteGrower}
                        />
                    </Grid2>
                )}
            </Grid2>
        </ModalV2>
    );
}

const NewGrowersModalV2 = ({
    open,
    setOpen,
    onAddFields,
    singleGrower = false,
    onGrowerAdded,
    defaultRv,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onAddFields: (growerUserNodeId?: number) => void;
    singleGrower?: boolean;
    onGrowerAdded?: (growerUserNodeId?: number) => void;
    defaultRv?: string;
}) => (
    <SingleGrowerFormProvider>
        <NewGrowersModalContent
            open={open}
            setOpen={setOpen}
            onAddFields={onAddFields}
            singleGrower={singleGrower}
            onGrowerAdded={onGrowerAdded}
            defaultRv={defaultRv}
        />
    </SingleGrowerFormProvider>
);

export default NewGrowersModalV2;
